/**
 * Dokumenten-Referenz für PDF/Export: Typ-Präfix (z. B. AN) + Jahr/Laufnummer aus display_id + fortlaufende Exportnummer.
 * Die CRM-display_id (z. B. ANF-26-00012) bleibt unverändert; nur das erste Segment wird durch den Dokumenttyp-Code ersetzt.
 */

import {
  buildDocumentNumber,
  detectDocumentNumberDelimiter,
  documentYearKey,
  type DocumentYearMode,
} from "./documentNumberFormat.ts";

export { isInvoiceFamilyDocumentType } from "./documentNumberFormat.ts";

/** Kurzcode pro Dokumenttyp (nicht identisch mit Lead-Präfix ANF/KVA/…). */
export const DOCUMENT_LABEL_EXPORT_CODE: Record<string, string> = {
  Angebot: 'AN',
  Kostenvoranschlag: 'KV',
  Auftragsbestätigung: 'AB',
  Lieferschein: 'LS',
  Rechnung: 'RE',
  Abschlagsrechnung: 'AR',
  Schlussrechnung: 'SR',
  Stornorechnung: 'ST',
  Gutschrift: 'GS',
  Arbeitsbericht: 'ARB',
  Checkliste: 'CL',
  Erinnerung: 'ER',
  '1. Mahnung': 'M1',
  '2. Mahnung': 'M2',
  '3. Mahnung': 'M3',
};

export function documentTypeLabelToExportCode(documentTypeLabel: string): string {
  const t = String(documentTypeLabel ?? '').trim();
  if (DOCUMENT_LABEL_EXPORT_CODE[t]) return DOCUMENT_LABEL_EXPORT_CODE[t];
  const compact = t.replace(/[^a-z0-9]+/gi, '').toUpperCase();
  return compact.slice(0, 3) || 'DOC';
}

const INVOICE_FAMILY_CODES = new Set(['RE', 'AR', 'SR', 'ST', 'GS']);

export function isInvoiceFamilyCode(code: string): boolean {
  return INVOICE_FAMILY_CODES.has(String(code ?? '').trim().toUpperCase());
}

export function currentDocumentYearSuffix(date: Date = new Date()): string {
  return String(date.getFullYear()).slice(-2);
}

/** Jahresschlüssel (YY/YYYY/„0") gemäß konfiguriertem Jahresformat. */
export function currentDocumentYearKey(yearMode: DocumentYearMode, date: Date = new Date()): string {
  return documentYearKey(yearMode, date);
}

export function buildYearlyInvoiceReferenceId(
  documentTypeLabel: string,
  yearKey: string,
  serial: number,
  format?: {
    prefix?: string;
    yearMode?: DocumentYearMode;
    seqDigits?: number;
    includeMonth?: boolean;
    monthKey?: string;
    delimiter?: string;
  },
): string {
  const prefix = format?.prefix ?? documentTypeLabelToExportCode(documentTypeLabel);
  const yearMode = format?.yearMode ?? 'yy';
  const seqDigits = format?.seqDigits ?? 5;
  return buildDocumentNumber({
    prefix,
    yearMode,
    yearKey,
    serial,
    seqDigits,
    includeMonth: format?.includeMonth,
    monthKey: format?.monthKey,
    delimiter: format?.delimiter,
  });
}

/**
 * @param displayId z. B. ANF-26-00012
 * @param serial fortlaufende Nummer pro Dokumenttyp (1, 2, …), wird als 01, 02 … formatiert
 * @param exportPrefix optionales Präfix-Override (Standard: DOCUMENT_LABEL_EXPORT_CODE)
 */
export function buildDocumentExportReferenceId(
  displayId: string,
  documentTypeLabel: string,
  serial: number,
  exportPrefix?: string,
  delimiter?: string,
): string {
  const trimmed = String(displayId ?? '').trim();
  const delim = detectDocumentNumberDelimiter(trimmed, delimiter);
  const code = exportPrefix ?? documentTypeLabelToExportCode(documentTypeLabel);
  const s = Math.max(1, Math.floor(Number(serial)) || 1);
  const serialPart = s < 100 ? String(s).padStart(2, '0') : String(s);

  if (!trimmed) {
    return code ? `${code}${delim}${serialPart}` : serialPart;
  }

  const parts = trimmed.split(delim).filter(Boolean);
  const firstIsNumeric = /^\d+$/.test(parts[0] ?? '');
  if (!code) {
    return [...parts, serialPart].join(delim);
  }
  if (firstIsNumeric || parts.length < 2) {
    return [code, ...parts, serialPart].join(delim);
  }
  return [code, ...parts.slice(1), serialPart].join(delim);
}

/** Erste Zeile der Kopfzeile: „Angebot AN-26-00012-01“. */
export function buildDocumentHeadingLine1(
  documentTypeLabel: string,
  displayId: string,
  serial: number,
): string {
  const ref = buildDocumentExportReferenceId(displayId, documentTypeLabel, serial);
  return `${documentTypeLabel} ${ref}`;
}
